import {createSelector} from 'reselect';

import {roundScore} from '@homeaway/react-review-components';
import get from 'lodash/get';

import {getListing, getListingPage, getTranslations, getTravelerFeatures} from './store';
import {labelMaker} from '../../server/utils/labelMaker';
import {getJsReviews, getSuperlative} from './reviews';
import {
    getAverageNightly,
    getConditionalInstantBook,
    getTotals,
    getPriceSummaryDetails,
    getPriceSummarySecondaryDetails
} from './priceDetails';
import {getTripNights} from './tripNights';
import {getIsMobile, getBrandName} from './system';
import {getInstallmentsBadge, getNldBadge} from './badges';
import getUnitId from '../utils/getUnitId';
import {
    getAverageRating,
    getBookingType,
    getGeography,
    getHostType,
    getListingIds,
    getListingHeadline,
    getMultiUnit,
    getPriceSummary,
    getSuperlativeBadges
} from './listing';
import {getPremierPartnerBadge} from './badges';
import {areBothDatesValid} from '../utils/dateHelper';
import {getPriceSummaryObjectByType} from '../utils/pricingHelper';

const PRICE_SUMMARY_TYPE_PER_NIGHT = 'PER_NIGHT';

const getBookingAssistance = createSelector(
    [getBookingType, getTranslations, getIsMobile, getBrandName],
    (bookingType, translations, isMobile, brandName) => {
        const {payPerBooking, onlineBookable} = bookingType;

        const {bookingAssistNum, bookingAssistPPSNum, bookingAssistBrand} = translations;

        let number = bookingAssistNum;

        if (!payPerBooking && onlineBookable) {
            number = bookingAssistPPSNum;
        }

        const htmlMobile = `<a class="booking-phone__a" href="tel:${number}"> ${number}</a>`;
        const htmlDesktop = `<strong>${number}</strong>`;
        const html = isMobile ? htmlMobile : htmlDesktop;
        const labelMakerParams = {
            number: html,
            brand: brandName
        };
        const transformed = labelMaker(bookingAssistBrand, labelMakerParams);

        return transformed;
    }
);

/* eslint-disable complexity */
const getPrice = createSelector(
    [
        getAverageNightly,
        getTotals,
        getTripNights,
        getTranslations,
        getTravelerFeatures,
        getListing,
        getListingPage,
        getPriceSummary,
        getPriceSummaryDetails,
        getPriceSummarySecondaryDetails
    ],
    (
        averageNightly,
        totals,
        tripNights,
        translations,
        travelerFeatures,
        listing,
        listingPage,
        priceSummaryDefault,
        priceSummary,
        priceSummarySecondary
    ) => {
        let {amount, label} = getPriceSummaryObjectByType(
            priceSummary,
            priceSummarySecondary,
            PRICE_SUMMARY_TYPE_PER_NIGHT
        );

        const pricingKey = listingPage.pricingKey;
        const priceSummaryEventParams = {
            unituuid: getUnitId(listing.unitApiUrl, false),
            eventstartdate: pricingKey.get('arrival'),
            eventenddate: pricingKey.get('departure'),
            eventnumadults: (pricingKey.get('adults') || 0).toString(),
            eventnumchildren: (pricingKey.get('children') || 0).toString(),
            eventpets: (pricingKey.get('pets') || false).toString(),
            eventcurrency: pricingKey.get('currency')
        };

        const validDates = areBothDatesValid(
            pricingKey.get('arrival'),
            pricingKey.get('departure')
        );

        if (validDates) {
            if (travelerFeatures.showTotal) {
                const totalAmount = totals.totalWithoutDeposits;

                if (totalAmount) {
                    amount = totalAmount;
                    label = labelMaker(translations.nightTotal, tripNights);
                }
            } else if (!amount || !label) {
                const nightlyAmount = travelerFeatures.includeTaxesAndFees
                    ? averageNightly.averageNightlyTotalTrip
                    : averageNightly.averageNightlyTotalRent;

                if (nightlyAmount) {
                    amount = nightlyAmount;
                    label = translations.perNight;
                }
            }
        }
        if ((!amount || !label) && priceSummaryDefault) {
            amount = priceSummaryDefault.formattedAmount;
            label = priceSummaryDefault.pricePeriodDescription;
        }

        // merge the final price and description into the EDAP event payload
        priceSummaryEventParams.stramount = amount;
        priceSummaryEventParams.perioddescription = label;

        return {
            amount,
            label,
            priceSummaryEventParams
        };
    }
);

const getLocation = createSelector(
    [getGeography],
    (geography) => {
        return get(geography, 'description') || null;
    }
);

const getReviewCount = createSelector(
    [getJsReviews],
    (reviewsData) => {
        return reviewsData.reviewCount;
    }
);

const getHeadline = createSelector(
    [getListingHeadline, getMultiUnit],
    (listingHeadline, multiUnit) => {
        const headline =
            multiUnit.isMultiUnit && multiUnit.unitName
                ? `${listingHeadline} - ${multiUnit.unitName}`
                : listingHeadline;

        return headline;
    }
);

const getListingSummary = createSelector(
    [
        getBookingAssistance,
        getPrice,
        getLocation,
        getListingPage,
        getPremierPartnerBadge,
        getTranslations,
        getSuperlative,
        getReviewCount,
        getConditionalInstantBook,
        getAverageRating,
        getHeadline,
        getBookingType,
        getHostType,
        getListingIds,
        getMultiUnit,
        getInstallmentsBadge,
        getNldBadge,
        getSuperlativeBadges
    ],
    (
        bookingAssistance,
        price,
        location,
        listingPage,
        premierPartnerBadge,
        translations,
        superlative,
        reviewCount,
        conditionalInstantBook,
        averageRating,
        headline,
        bookingType,
        hostType,
        listingIds,
        multiUnit,
        installmentsBadge,
        nldBadge,
        superlativeBadges
    ) => {
        let propertyNum = labelMaker(translations.propertyNum, {propertynumber: ''});

        const instantBookTooltip = hostType.owner
            ? translations.instantBookTooltipOwner
            : translations.instantBookTooltipManager;

        const labels = {
            reviews: labelMaker(translations.reviewCount, {REVIEWCOUNT: reviewCount}),
            averageNightly: translations.averageNightly,
            instantBook: translations.instantBook,
            instantBookTooltip,
            instantConfirmation: translations.instantConfirmation,
            perNight: translations.perNight,
            map: translations.navMap,
            priceLabel: price.label,
            propertyNum,
            bookingAssist: bookingAssistance,
            unitNum: translations.unitNum,
            skipToBookingDetails: translations.skipToBookingDetails,
            skipToBookingDetailsAnchour: translations.skipToBookingDetailsAnchour
        };

        const isPremier = !!(premierPartnerBadge && premierPartnerBadge.name);

        return {
            superlative,
            headline,
            location,
            premierPartner: premierPartnerBadge,
            propertyOlb: bookingType.onlineBookable,
            reviewCountLabel: labels.reviews,
            rating: averageRating,
            ratingRounded: roundScore(averageRating),
            loadingRate: listingPage.loadingRate,
            instantBookable: bookingType.instantBook || conditionalInstantBook,
            multiUnit: multiUnit.isMultiUnit,
            listingId: listingIds.listingId,
            propertyId: listingIds.propertyId,
            unitNumber: multiUnit.unitNumber,
            reviewCount,
            price: price.amount,
            priceSummaryEventParams: price.priceSummaryEventParams,
            installmentsBadge,
            nldBadge,
            labels,
            superlativeBadges,
            isPremier
        };
    }
);

export default getListingSummary;
export {
    getListingSummary,
    getLocation,
    getBookingAssistance,
    getPrice,
    getReviewCount,
    getHeadline,
    PRICE_SUMMARY_TYPE_PER_NIGHT
};
